// TechnicianInsightsPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import Hoot from "../../Hoot";

const normalize = (str) => (str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "");
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (n) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "AOA", maximumFractionDigits: 0 }).format(safeNum(n));
const fmtDateInput = (d) => {
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const mapTech = (t) => ({
  id: t?.ed_tech_id ?? t?.tech_id ?? t?.id,
  name: t?.ed_tech_name ?? t?.tech_name ?? t?.name,
  email: t?.ed_tech_email ?? t?.tech_email ?? t?.email,
  status: t?.ed_tech_status ?? t?.tech_status ?? t?.status ?? "ativo",
  province: t?.ed_tech_province ?? t?.tech_province ?? t?.province ?? "",
  area: t?.ed_tech_area ?? t?.tech_area ?? t?.area ?? "",
});

const statusOptions = [
  { label: "Ativo", value: "ativo" },
  { label: "Inativo", value: "inativo" },
  { label: "Férias", value: "ferias" },
];

const ui = `
.tech-insights{
  background:#fff;
  border:1px solid rgba(226,232,240,.95);
  border-radius:18px;
  padding:12px 14px 14px;
  box-shadow: 0 14px 40px rgba(15,23,42,.08);
}
.tech-insights .topbar{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:10px;
}
.tech-insights .title{
  display:flex;
  align-items:center;
  gap:10px;
}
.tech-insights .title h6{
  margin:0;
  font-weight:700;
  color:#0f172a;
}
.tech-insights .sub{
  margin-top:3px;
  color:#64748b;
  font-size:.82rem;
}
.tech-insights .filters{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:end;
}
.tech-insights .kpis{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
  margin:10px 0 12px;
}
 
 
.tech-insights .kpi{
  background:#fff;
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px;
  padding:10px 12px;
  box-shadow: 0 10px 28px rgba(2,6,23,.06);
  position:relative;
  overflow:hidden;
}
.tech-insights .kpi::before{
  content:"";
  position:absolute;
  inset:-40px -60px auto auto;
  width:120px;height:120px;border-radius:999px;
  background: radial-gradient(circle at 30% 30%, rgba(91,92,255,.18), transparent 60%);
}
.tech-insights .kpi .kpi-t{ font-size:.78rem; color:#64748b; font-weight:650; display:flex; align-items:center; gap:8px; }
.tech-insights .kpi .kpi-v{ margin-top:2px; font-size:1.1rem; font-weight:800; color:#0f172a; }
.tech-insights .kpi .kpi-s{ margin-top:2px; font-size:.74rem; color:#64748b; }
.tech-insights .grid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap:12px;
}
@media (max-width: 920px){
  .tech-insights .grid{ grid-template-columns: 1fr; }
}
.tech-insights .cardlet{
  background: rgba(255,255,255,.95);
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px;
  padding:10px 12px;
}
.tech-insights .cardlet h6{
  margin:0 0 8px;
  font-weight:750;
  color:#0f172a;
  font-size:.92rem;
  display:flex;
  gap:8px;
  align-items:center;
}
.tech-insights .table-smx{
  width:100%;
  border-collapse:collapse;
  font-size:.84rem;
}
.tech-insights .table-smx th{
  text-align:left;
  font-size:.75rem;
  color:#64748b;
  font-weight:700;
  border-bottom:1px solid rgba(226,232,240,.95);
  padding:8px 8px;
}
.tech-insights .table-smx td{
  border-bottom:1px solid rgba(226,232,240,.95);
  padding:8px 8px;
  vertical-align:middle;
}
.tech-insights .muted{ color:#64748b; }
.tech-insights .pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(226,232,240,.55);
  font-size:.74rem;
  font-weight:700;
  color:#0f172a;
}
`;

export default function TechnicianInsightsPanel({
  showStatusBreakdown = true,
  showProvinceBreakdown = true,
  showCharts = true,
  title = "Insights (Formações & Apresentações)",
}) {
  const API_TECH = useMemo(() => Hoot() + "api/technicians", []);
  const API_TRAININGS = useMemo(() => Hoot() + "api/tech-trainings", []);
  const API_USER_ACTIVITIES = (userId, from, to) => Hoot() + `users/${userId}/activities?from=${from}&to=${to}`;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const now = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => fmtDateInput(addDays(now, -30)));
  const [to, setTo] = useState(() => fmtDateInput(now));

  const [techs, setTechs] = useState([]);
  const [trainingsByTech, setTrainingsByTech] = useState({});
  const [activitiesByTech, setActivitiesByTech] = useState({});

  const abortRef = useRef(false);

  const fetchAll = async () => {
    setLoading(true);
    setErr(null);

    try {
      const r = await axios.get(API_TECH, { withCredentials: true });
      const rows = r?.data?.data ?? r?.data ?? [];
      const list = Array.isArray(rows) ? rows.map(mapTech).filter((t) => t.id) : [];
      if (abortRef.current) return;
      setTechs(list);

      const limitConcurrency = async (items, worker, limit = 6) => {
        const res = [];
        let idx = 0;
        const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
          while (idx < items.length) {
            const i = idx++;
            res[i] = await worker(items[i], i);
          }
        });
        await Promise.all(runners);
        return res;
      };

      const trainingsResults = {};
      await limitConcurrency(
        list,
        async (t) => {
          try {
            const tr = await axios.get(`${API_TRAININGS}?tech_id=${t.id}`, { withCredentials: true });
            const arr = tr?.data ?? [];
            trainingsResults[t.id] = Array.isArray(arr) ? arr : [];
          } catch {
            trainingsResults[t.id] = [];
          }
        },
        6
      );
      if (abortRef.current) return;
      setTrainingsByTech(trainingsResults);

      const activitiesResults = {};
      await limitConcurrency(
        list,
        async (t) => {
          try {
            const ar = await axios.get(API_USER_ACTIVITIES(t.id, from, to), { withCredentials: true });
            const arr = ar?.data?.data ?? [];
            activitiesResults[t.id] = Array.isArray(arr) ? arr : [];
          } catch {
            activitiesResults[t.id] = [];
          }
        },
        6
      );
      if (abortRef.current) return;
      setActivitiesByTech(activitiesResults);
    } catch (e) {
      setErr(e?.response?.data?.msg || "Erro ao carregar insights.");
      setTechs([]);
      setTrainingsByTech({});
      setActivitiesByTech({});
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    abortRef.current = false;
    fetchAll();
    return () => {
      abortRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    abortRef.current = false;
    fetchAll();
    return () => {
      abortRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const computed = useMemo(() => {
    const rows = techs.map((t) => {
      const trs = trainingsByTech[t.id] || [];
      const acts = activitiesByTech[t.id] || [];

      const trainingsTotal = trs.length;
      const trainingsDone = trs.filter((x) => String(x?.tr_status || "").toUpperCase() === "DADA").length;

      const cost = trs.reduce((acc, x) => {
        const c = safeNum(x?.tr_cost_transport) + safeNum(x?.tr_cost_food) + safeNum(x?.tr_cost_other);
        return acc + c;
      }, 0);

      const uniqTrainingSchools = new Set(
        trs.map((x) => x?.tr_institute_code || x?.institute_code || x?.instituteCode).filter(Boolean)
      ).size;

      const presActs = acts.filter((a) => normalize(a?.meeting_type || a?.meetingType || "") === "apresentacao");
      const presentationsTotal = presActs.length;

      const uniqPresentationSchools = new Set(
        presActs.map((a) => a?.meeting_contact_id || a?.meetingContactId).filter(Boolean)
      ).size;

      return {
        tech_id: t.id,
        name: t.name,
        status: t.status,
        province: t.province,
        trainings_total: trainingsTotal,
        trainings_done: trainingsDone,
        trainings_schools: uniqTrainingSchools,
        trainings_cost: cost,
        presentations_total: presentationsTotal,
        presentations_schools: uniqPresentationSchools,
      };
    });

    const totals = rows.reduce(
      (acc, r) => {
        acc.techs += 1;
        acc.trainings += r.trainings_total;
        acc.trainingsDone += r.trainings_done;
        acc.presentations += r.presentations_total;
        acc.cost += r.trainings_cost;
        acc.trainingSchools += r.trainings_schools;
        acc.presentationSchools += r.presentations_schools;
        return acc;
      },
      { techs: 0, trainings: 0, trainingsDone: 0, presentations: 0, cost: 0, trainingSchools: 0, presentationSchools: 0 }
    );

    const byStatus = rows.reduce((m, r) => {
      const key = normalize(r.status) || "outro";
      if (!m[key]) m[key] = { techs: 0, trainings: 0, presentations: 0, cost: 0 };
      m[key].techs += 1;
      m[key].trainings += r.trainings_total;
      m[key].presentations += r.presentations_total;
      m[key].cost += r.trainings_cost;
      return m;
    }, {});

    const byProvince = rows.reduce((m, r) => {
      const key = r.province || "N/A";
      if (!m[key]) m[key] = { techs: 0, trainings: 0, presentations: 0, cost: 0 };
      m[key].techs += 1;
      m[key].trainings += r.trainings_total;
      m[key].presentations += r.presentations_total;
      m[key].cost += r.trainings_cost;
      return m;
    }, {});

    const topByCost = [...rows].sort((a, b) => b.trainings_cost - a.trainings_cost).slice(0, 8);
    const topByTrainings = [...rows].sort((a, b) => b.trainings_total - a.trainings_total).slice(0, 8);
    const topByPresentations = [...rows].sort((a, b) => b.presentations_total - a.presentations_total).slice(0, 8);

    return { rows, totals, byStatus, byProvince, topByCost, topByTrainings, topByPresentations };
  }, [techs, trainingsByTech, activitiesByTech]);

  const costChart = useMemo(() => {
    const cats = computed.topByCost.map((r) => r.name || `#${r.tech_id}`);
    const series = [{ name: "Custos (Formações)", data: computed.topByCost.map((r) => Math.round(r.trainings_cost)) }];
    const options = {
      chart: { type: "bar", toolbar: { show: false }, height: 320 },
      plotOptions: { bar: { borderRadius: 8, columnWidth: "52%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: cats, labels: { rotate: -30 } },
      grid: { borderColor: "#e2e8f0" },
      tooltip: { theme: "light", y: { formatter: (v) => fmtMoney(v) } },
      legend: { show: false },
    };
    return { options, series };
  }, [computed.topByCost]);

  const trainingsChart = useMemo(() => {
    const cats = computed.topByTrainings.map((r) => r.name || `#${r.tech_id}`);
    const series = [{ name: "Formações", data: computed.topByTrainings.map((r) => r.trainings_total) }];
    const options = {
      chart: { type: "bar", toolbar: { show: false }, height: 320 },
      plotOptions: { bar: { borderRadius: 8, columnWidth: "52%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: cats, labels: { rotate: -30 } },
      grid: { borderColor: "#e2e8f0" },
      tooltip: { theme: "light", y: { formatter: (v) => `${v} formações` } },
      legend: { show: false },
    };
    return { options, series };
  }, [computed.topByTrainings]);

  const presChart = useMemo(() => {
    const cats = computed.topByPresentations.map((r) => r.name || `#${r.tech_id}`);
    const series = [{ name: "Apresentações", data: computed.topByPresentations.map((r) => r.presentations_total) }];
    const options = {
      chart: { type: "bar", toolbar: { show: false }, height: 320 },
      plotOptions: { bar: { borderRadius: 8, columnWidth: "52%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: cats, labels: { rotate: -30 } },
      grid: { borderColor: "#e2e8f0" },
      tooltip: { theme: "light", y: { formatter: (v) => `${v} apresentações` } },
      legend: { show: false },
    };
    return { options, series };
  }, [computed.topByPresentations]);

  const statusTable = useMemo(() => {
    const keys = Object.keys(computed.byStatus);
    const rows = keys
      .map((k) => ({
        key: k,
        label: statusOptions.find((x) => normalize(x.value) === normalize(k))?.label || k,
        ...computed.byStatus[k],
      }))
      .sort((a, b) => b.techs - a.techs);
    return rows;
  }, [computed.byStatus]);

  const provinceTable = useMemo(() => {
    const keys = Object.keys(computed.byProvince);
    const rows = keys
      .map((k) => ({ key: k, ...computed.byProvince[k] }))
      .sort((a, b) => b.techs - a.techs);
    return rows;
  }, [computed.byProvince]);

  return (
    <>
      <style>{ui}</style>

      <div className="tech-insights">
        <div className="topbar">
          <div className="title">
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                display: "grid",
                placeItems: "center",
                background: "rgba(91,92,255,.12)",
                border: "1px solid rgba(91,92,255,.18)",
                color: "#5b5cff",
                fontWeight: 900,
              }}
            >
              <i className="bi bi-graph-up" />
            </div>
            <div>
              <h6>{title}</h6>
              <div className="sub">
                Janela de análise: <b>{from}</b> → <b>{to}</b>{" "}
                <Badge bg="light" text="dark" style={{ borderRadius: 999, border: "1px solid rgba(226,232,240,.95)" }}>
                  {computed.totals.techs} técnicos
                </Badge>
              </div>
            </div>
          </div>

          <div className="filters">
            <Form.Group>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>De</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Até</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} />
            </Form.Group>
            <Button
              variant="outline-dark"
              className="rounded-pill"
              disabled={loading}
              onClick={() => fetchAll()}
              style={{ borderRadius: 999 }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  a carregar…
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1" />
                  Recarregar
                </>
              )}
            </Button>
          </div>
        
        </div>
           <br />

        {err ? (
          <Alert variant="danger" style={{ borderRadius: 14, marginBottom: 10 }}>
            {err}
          </Alert>
        ) : null}

        <div className="kpis">
          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-mortarboard" /> Formações
            </div>
            <div className="kpi-v">{computed.totals.trainings}</div>
            <div className="kpi-s">{computed.totals.trainingsDone} dadas</div>
          </div>

          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-cash-coin" /> Custos
            </div>
            <div className="kpi-v">{fmtMoney(computed.totals.cost)}</div>
            <div className="kpi-s">logística (formações)</div>
          </div>

          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-easel2" /> Apresentações
            </div>
            <div className="kpi-v">{computed.totals.presentations}</div>
            <div className="kpi-s">na janela</div>
          </div>

          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-buildings" /> Escolas (formações)
            </div>
            <div className="kpi-v">{computed.totals.trainingSchools}</div>
            <div className="kpi-s">únicas (por técnico somado)</div>
          </div>

          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-building-check" /> Escolas (apresentações)
            </div>
            <div className="kpi-v">{computed.totals.presentationSchools}</div>
            <div className="kpi-s">únicas (por técnico somado)</div>
          </div>

          <div className="kpi">
            <div className="kpi-t">
              <i className="bi bi-people" /> Média custos/técnico
            </div>
            <div className="kpi-v">
              {computed.totals.techs ? fmtMoney(computed.totals.cost / computed.totals.techs) : fmtMoney(0)}
            </div>
            <div className="kpi-s">aprox.</div>
          </div>
        </div>

        <div className="block">
          {showCharts ? (
            <div className="cardlet">
              <h6>
                <i className="bi bi-currency-exchange" /> Top custos por técnico
              </h6>
              {loading ? (
                <div className="muted d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" /> a carregar…
                </div>
              ) : computed.topByCost.length ? (
                <ReactApexChart options={costChart.options} series={costChart.series} type="bar" height={320} width="100%" />
              ) : (
                <div className="muted">Sem dados.</div>
              )}
            </div>
          ) : null}

          {showCharts ? (
            <div className="cardlet">
              <h6>
                <i className="bi bi-mortarboard" /> Top formações por técnico
              </h6>
              {loading ? (
                <div className="muted d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" /> a carregar…
                </div>
              ) : computed.topByTrainings.length ? (
                <ReactApexChart
                  options={trainingsChart.options}
                  series={trainingsChart.series}
                  type="bar"
                  height={320}
                  width="100%"
                />
              ) : (
                <div className="muted">Sem dados.</div>
              )}
            </div>
          ) : null}

          {showCharts ? (
            <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
              <h6>
                <i className="bi bi-easel2" /> Top apresentações por técnico
              </h6>
              {loading ? (
                <div className="muted d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" /> a carregar…
                </div>
              ) : computed.topByPresentations.length ? (
                <ReactApexChart options={presChart.options} series={presChart.series} type="bar" height={320} width="100%" />
              ) : (
                <div className="muted">Sem dados.</div>
              )}
            </div>
          ) : null}

          {showStatusBreakdown ? (
            <div className="cardlet">
              <h6>
                <i className="bi bi-shield-check" /> Resumo por estado
              </h6>
              <table className="table-smx">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Técnicos</th>
                    <th>Formações</th>
                    <th>Apresentações</th>
                    <th>Custos</th>
                  </tr>
                </thead>
                <tbody>
                  {statusTable.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <span className="pill">{r.label}</span>
                      </td>
                      <td>{r.techs}</td>
                      <td>{r.trainings}</td>
                      <td>{r.presentations}</td>
                      <td>{fmtMoney(r.cost)}</td>
                    </tr>
                  ))}
                  {!statusTable.length ? (
                    <tr>
                      <td colSpan={5} className="muted">
                        Sem dados.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {showProvinceBreakdown ? (
            <div className="cardlet">
              <h6>
                <i className="bi bi-geo-alt" /> Resumo por província
              </h6>
              <table className="table-smx">
                <thead>
                  <tr>
                    <th>Província</th>
                    <th>Técnicos</th>
                    <th>Formações</th>
                    <th>Apresentações</th>
                    <th>Custos</th>
                  </tr>
                </thead>
                <tbody>
                  {provinceTable.map((r) => (
                    <tr key={r.key}>
                      <td style={{ fontWeight: 800 }}>{r.key}</td>
                      <td>{r.techs}</td>
                      <td>{r.trainings}</td>
                      <td>{r.presentations}</td>
                      <td>{fmtMoney(r.cost)}</td>
                    </tr>
                  ))}
                  {!provinceTable.length ? (
                    <tr>
                      <td colSpan={5} className="muted">
                        Sem dados.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}